import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Star, Award, Trophy, Crown, Flame, Circle, Target, Zap, Lock } from "lucide-react";
import { apiFetch } from "@/lib/apiClient";

const ICON_MAP = {
  star: Star,
  award: Award,
  trophy: Trophy,
  crown: Crown,
  flame: Flame,
  circle: Circle,
  target: Target,
  zap: Zap
};

const TrophyModal = ({ open, onOpenChange }) => {
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchTrophies();
    }
  }, [open]);

  const fetchTrophies = async () => {
    try {
      const response = await apiFetch('/trophies');
      if (response.ok) {
        const data = await response.json();
        setTrophies(data);
      }
    } catch (error) {
      console.error('Error fetching trophies:', error);
    } finally {
      setLoading(false);
    }
  };

  const earnedCount = trophies.filter(t => t.earned).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="trophy-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            Trophy Case
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {earnedCount} of {trophies.length} trophies earned
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="trophy-grid">
              {trophies.map(trophy => {
                const IconComponent = ICON_MAP[trophy.icon] || Trophy;
                return (
                  <div
                    key={trophy.id}
                    className={`
                      p-4 rounded-xl border text-center
                      ${trophy.earned 
                        ? 'bg-amber-400/20 border-amber-400' 
                        : 'bg-muted/30 border-border opacity-60'
                      }
                    `}
                    data-testid={`trophy-${trophy.id}`}
                  >
                    <div className="relative mx-auto w-10 h-10 mb-2">
                      {trophy.earned ? (
                        <IconComponent className="w-10 h-10 text-amber-500" />
                      ) : (
                        <>
                          <IconComponent className="w-10 h-10 text-muted-foreground/40" />
                          <Lock className="w-4 h-4 absolute -bottom-1 -right-1 text-muted-foreground" />
                        </>
                      )}
                    </div>
                    <p className={`text-xs font-medium ${trophy.earned ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {trophy.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {trophy.description}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TrophyModal;
